/**
 * Every visibility / disabled / tooltip input the toolbar descriptors need.
 *
 * Moved verbatim out of `MainToolbar.tsx` by task B11 — the predicates here
 * ARE the INJ-2 fix for Jean-Luc's "thin, greyed-out More menu", so they must
 * keep keying off "is ANY connected connection eligible" rather than the tree
 * selection. Narrowing any of them back to `selectedConnectionId` re-opens a
 * closed bug.
 */
import { useMemo } from 'react';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { useDatabaseUIStore } from '../../database-objects/store/uiStore';
import { useDatabaseStore } from '../../database-objects/store/databaseStore';
import { useFirstConnectCoachStore } from '../../workspace/first-connect-bridge';
import { isBrowserMode } from '../../browser-mode';
import type { ContextState } from '../../toolbar-context';
import {
  hasJobSchedulerSupport,
  getJobSchedulerToolbarLabel,
  useJobSchedulerEngineSupportVersion,
} from '../../workspace/job-scheduler/engineConfig';
import { findNodeById } from '../../explorer/services/treeService';
import {
  supportsMssqlDbaEngineFilter,
  supportsNotebooksEngineFilter,
  supportsOracleDbaEngineFilter,
  supportsQueryEditorEngineFilter,
  supportsSchemaOverviewEngineFilter,
  supportsSpatialExplorerEngineFilter,
  supportsTableExplorerEngineFilter,
} from '../model/toolbarEngineFilters';
import type { ToolbarGating } from '../model/toolbarItemTypes';
import { dataImportUnsupportedReason, resolveDataImportConnection } from '../../workspace/data-import/mapping/targetEligibility';
import { useAppStore } from '../../../store/appStore';
import { resolveAllBetaFeatures } from '../../../core/beta';

export function useToolbarGating() {
  const firstConnectCoachVisible = useFirstConnectCoachStore((s) => s.visible);
  const { selectedConnectionId, selectedDatabase, getValidSelectedDatabase, selectedNodeId } =
    useDatabaseUIStore();
  const { treeData } = useDatabaseStore();
  const { connections, activeConnections } = useConnectionStore();
  const jobSchedulerSupportVersion = useJobSchedulerEngineSupportVersion();
  // Subscribed (not a snapshot read): ticking a box in Settings → Beta
  // Features must add or remove the button while the toolbar stays mounted.
  const betaOverrides = useAppStore((state) => state.betaFeatures);
  const betaEnabled = useMemo(() => resolveAllBetaFeatures(betaOverrides), [betaOverrides]);

  const validDbSelection = getValidSelectedDatabase();

  // Compute context state for dynamic tooltips
  const connectedConnections = activeConnections.filter((c) => c.status === 'connected');
  const contextState: ContextState = useMemo(() => ({
    hasConnection: connectedConnections.length > 0,
    hasDatabase: !!validDbSelection?.database,
  }), [connectedConnections.length, validDbSelection?.database]);

  // Same "selected, else first connected" fallback AiToolbarButton's
  // getSelectedConnection already uses (ai-toolbar/components/AiToolbarButton.tsx)
  // — resolves a concrete connection for actions that need one (Open SQL File /
  // Import Data) without requiring a tree click first. Safe when ambiguous (2+
  // connected, none selected): both consumers only ever *seed a default* —
  // the opened query tab shows its connection badge and stays freely
  // reassignable, and Import Data lands on the wizard's Target step, which
  // requires explicit confirmation before anything writes (INJ-2).
  const fallbackConnectionId = selectedConnectionId ?? connectedConnections[0]?.id;

  // Get explorer selected table info for Table Designer quick access
  // Look up the node in the tree to get the table name and schema from metadata
  const explorerSelectedTable = useMemo(() => {
    if (!selectedNodeId || !validDbSelection) return undefined;

    const node = findNodeById(selectedNodeId, treeData);
    if (!node || node.type !== 'table') return undefined;

    // Table nodes have metadata: { objectDetails: DatabaseObject }
    // DatabaseObject has: connectionId, name, schema, type
    type TableNodeMetadata = { objectDetails?: { name?: string; schema?: string; connectionId?: string } };
    const metadata = node.metadata as TableNodeMetadata | undefined;
    const objectDetails = metadata?.objectDetails;

    const tableName = objectDetails?.name || node.name;
    const schema = objectDetails?.schema ?? 'dbo';
    const database = validDbSelection.database;

    if (tableName && database) {
      return { name: tableName, schema, database };
    }
    return undefined;
  }, [selectedNodeId, treeData, validDbSelection]);

  // Check if the selected connection is Oracle
  const isOracleConnection = useMemo(() => {
    if (!selectedConnectionId) return false;
    const activeConn = activeConnections.find((c) => c.id === selectedConnectionId);
    return activeConn?.connection?.engine === 'oracle';
  }, [selectedConnectionId, activeConnections]);

  // Whether ANY connected connection (not just the resolved/selected one)
  // supports the DBA group — the same "ANY connected" technique the command
  // palette already uses to gate this exact set of items (workspaceCommands.ts:
  // isAnySessionBrowserEngineConnected / isAnyPerformanceDashboardEngineConnected,
  // both oracle|mssql|pgsql|mysql|undefined). A user with 2+ connections and no
  // tree selection now sees the group.
  const hasDbaEligibleConnection = useMemo(() => (
    activeConnections.some((c) => {
      if (c.status !== 'connected') return false;
      const engine = c.connection?.engine;
      return engine === 'oracle' || engine === 'mssql' || engine === 'pgsql' || engine === 'mysql' || engine === undefined;
    })
  ), [activeConnections]);

  // Same "any connected" technique, narrowed to each nested item's own engine.
  // Each predicate is the SAME function passed as the matching item's
  // `engineFilter`, so visibility and click-time targeting can never disagree
  // (INJ-2 round 2).
  const hasAnyOracleConnection = useMemo(() => (
    activeConnections.some((c) => c.status === 'connected' && supportsOracleDbaEngineFilter(c.connection?.engine))
  ), [activeConnections]);

  const hasAnyMssqlConnection = useMemo(() => (
    activeConnections.some((c) => c.status === 'connected' && supportsMssqlDbaEngineFilter(c.connection?.engine))
  ), [activeConnections]);

  // Whether the toolbar's currently selected connection's engine lacks
  // Notebook / Schema Overview / Spatial support (kusto today).
  const selectedConnectionEngine = useMemo(() => {
    if (!selectedConnectionId) return undefined;
    return activeConnections.find((c) => c.id === selectedConnectionId)?.connection?.engine;
  }, [selectedConnectionId, activeConnections]);

  // Whether the selected connection supports job-scheduler, or else — same
  // "any connected" technique as above — whether some OTHER connected
  // connection does, so Agent Jobs is visible with 2+ connections and no tree
  // selection (INJ-2 round 2). The label always tracks whichever connection
  // will actually be resolved, so it is never blank while the item is visible.
  const { hasJobScheduler, jobSchedulerMenuLabel } = useMemo(() => {
    const selectedConn = activeConnections.find((c) => c.id === selectedConnectionId);
    const labelConn = (selectedConn && hasJobSchedulerSupport(selectedConn.connection?.engine))
      ? selectedConn
      : activeConnections.find((c) => c.status === 'connected' && hasJobSchedulerSupport(c.connection?.engine));
    return {
      hasJobScheduler: !!labelConn,
      jobSchedulerMenuLabel: labelConn ? getJobSchedulerToolbarLabel(labelConn.connection?.engine) : '',
    };
  }, [selectedConnectionId, activeConnections, jobSchedulerSupportVersion]);

  const importDataConnection = useMemo(
    () => resolveDataImportConnection(selectedConnectionId, activeConnections),
    [selectedConnectionId, activeConnections],
  );

  const gating: ToolbarGating = {
    contextState,
    firstConnectCoachVisible,
    browserMode: isBrowserMode(),
    hasFallbackConnection: !!fallbackConnectionId,
    notebooksUnsupported: selectedConnectionEngine
      ? !supportsNotebooksEngineFilter(selectedConnectionEngine)
      : false,
    schemaOverviewUnsupported: selectedConnectionEngine
      ? !supportsSchemaOverviewEngineFilter(selectedConnectionEngine)
      : false,
    spatialExplorerUnsupported: selectedConnectionEngine
      ? !supportsSpatialExplorerEngineFilter(selectedConnectionEngine)
      : false,
    // Kusto and Azure Storage have no SQL table to import into. Read off the connection the action itself resolves
    // (`resolveDataImportConnection`: the selected one, else a connected import target), so the item and Cmd/Ctrl+I agree.
    importDataUnsupportedReason: importDataConnection
      ? dataImportUnsupportedReason(importDataConnection.connection?.engine)
      : undefined,
    // S1 Task 1.16 — Azure Storage has no query language and no SQL tables.
    queryEditorUnsupported: selectedConnectionEngine
      ? !supportsQueryEditorEngineFilter(selectedConnectionEngine)
      : false,
    tableExplorerUnsupported: selectedConnectionEngine
      ? !supportsTableExplorerEngineFilter(selectedConnectionEngine)
      : false,
    selectedConnectionEngine,
    hasDbaEligibleConnection,
    hasAnyOracleConnection,
    hasAnyMssqlConnection,
    hasJobScheduler,
    jobSchedulerMenuLabel,
    betaEnabled,
  };

  return {
    gating,
    fallbackConnectionId,
    isOracleConnection,
    explorerSelectedTable,
    validDbSelection,
    selectedConnectionId,
    selectedDatabase,
    connections,
    activeConnections,
  };
}
